import React from "react"
import { useSelector } from "react-redux"
import EmploymentGoalsDashboard from "./EmploymentGoalsDashboard"
import { Card, CardBody, CardTitle, Col, Row } from "reactstrap"
import { useTranslation } from "react-i18next"
import { dountChartBgColor, dountChartHoverBgColor } from "./dashboardColors"
import { hasValidChartData } from "./dashboardChartOptions"
import DountChart from "pages/Chartsjs/dountchart"
import BarChart from "./barchart"
import { layoutModeTypes } from "constants/layout"
import Colors from "constants/Colors"

const RecruitmentStatistics = ({ filters }) => {
  const { loading, isLoading, dashboardData } = useSelector(
    state => state.Dashboard
  )

  const layoutMode = useSelector(state => state.Layout.layoutModeType)

  const {
    t,
    i18n: { language },
  } = useTranslation()

  const getChartData = (stats, labelKey) => ({
    labels:
      stats?.map(item =>
        language === "sa" ? item[`${labelKey}_ar`] : item[labelKey]
      ) || [],
    datasets: [
      {
        data: stats?.map(item => item.count) || [],
        backgroundColor: dountChartBgColor,
        hoverBackgroundColor: dountChartHoverBgColor,
        borderRadius: 7,
      },
    ],
  })

  const interviewOverviews = [
    {
      id: 1,
      title: "Promotions",
      count: dashboardData?.count_interviews_overview?.total_promotions,
    },
    {
      id: 2,
      title: "Rejection",
      count:
        dashboardData?.count_interviews_overview?.total_rejected_interviews,
    },
    {
      id: 3,
      title: "Selected",
      count:
        dashboardData?.count_interviews_overview?.total_accepted_interviews,
    },
    {
      id: 4,
      title: "Absence",
      count: dashboardData?.count_interviews_overview?.total_absences,
    },
    {
      id: 5,
      title: "Interviews Conducted",
      count:
        dashboardData?.count_interviews_overview?.total_interviews_completed,
    },
  ]

  const expNonExpChartData = {
    labels: [t("Experience"), t("New")],
    datasets: [
      {
        data: [
          dashboardData?.experienced_or_new_stats?.experienced_count || 0,
          dashboardData?.experienced_or_new_stats?.new_count || 0,
        ],
        backgroundColor: dountChartBgColor,
        hoverBackgroundColor: dountChartHoverBgColor,
        borderRadius: 7,
      },
    ],
  }

  const saudiNonSaudiChartData = {
    labels: [t("Saudi"), t("Non Saudi")],
    datasets: [
      {
        data: [
          dashboardData?.saudi_or_non_saudi_stats?.total_saudi_candidate || 0,
          dashboardData?.saudi_or_non_saudi_stats?.total_non_saudi_candidate ||
            0,
        ],
        backgroundColor: dountChartBgColor,
        hoverBackgroundColor: dountChartHoverBgColor,
        borderRadius: 7,
      },
    ],
  }
  const citiesChartData = getChartData(dashboardData?.city_stats, "city_name")
  const nationalitiesChartData = getChartData(
    dashboardData?.nationalities_stats,
    "nationality_name"
  )

  const chartConfigs = [
    { title: "Saudi and Non Saudi", data: saudiNonSaudiChartData },
    { title: "Cities", data: citiesChartData },
    { title: "Nationalities", data: nationalitiesChartData },
  ]
  //   AGE:
  const data = {
    labels: dashboardData?.ages_stats?.map(a => a?.age) || [],
    datasets: [
      {
        label: t("Total Count"),
        data: dashboardData?.ages_stats?.map(a => a.count) || [],
        backgroundColor: "#00778B",
      },
    ],
  }

  const options = {
    indexAxis: "y",
    plugins: {
      datalabels: {
        anchor: "end",
        align: "end",
        color: "#fff",
        formatter: (value, ctx) => {
          const total = ctx.dataset.data.reduce((sum, num) => sum + num, 0)
          const percentage = ((value / total) * 100).toFixed(1) + "%"
          return `${value} (${percentage})`
        },
      },
      legend: {
        onClick: null,
        labels: {
          color:
            layoutMode === layoutModeTypes.LIGHT ? Colors.black : Colors.white,
        },
      },
    },
    responsive: true,
    scales: {
      x: {
        grid: {
          color: "#f1f1f1",
        },
        ticks: {
          color:
            layoutMode === layoutModeTypes.LIGHT ? Colors.black : Colors.white,
        },
      },
      y: {
        grid: {
          color: "#f1f1f1",
        },
        ticks: {
          color:
            layoutMode === layoutModeTypes.LIGHT ? Colors.black : Colors.white,
        },
      },
    },
  }

  const dountChartOptions = {
    cutout: "80%",
    plugins: {
      legend: {
        onClick: null,
        position: "bottom",
        labels: {
          usePointStyle: true,
          boxWidth: 10,
          boxHeight: 10,
          padding: 5,
          font: { size: 12 },
          generateLabels: chart => {
            const { data } = chart
            return data.labels.map((label, i) => ({
              text: `${label} ${data.datasets[0].data[i].toLocaleString()}`, // Adds commas
              fillStyle: data.datasets[0].backgroundColor[i],
              fontColor:
                layoutMode === layoutModeTypes.LIGHT
                  ? Colors.black
                  : Colors.white,
              hidden: false,
              index: i,
              strokeStyle: data.datasets[0].backgroundColor[i],
              pointStyle: "rect",
            }))
          },
        },
      },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            const dataset = tooltipItem.dataset.data
            const total = dataset.reduce((acc, num) => acc + num, 0)
            const value = dataset[tooltipItem.dataIndex]
            const percentage = ((value / total) * 100).toFixed(1)

            return [
              `${t("Total Count")}: ${value.toLocaleString()}`,
              `${t("Percentage")}: ${percentage}%`,
            ]
          },
        },
      },
      datalabels: {
        display: false,
      },
    },
  }

  return (
    <>
      {loading ? (
        ""
      ) : (
        <>
          <Row data-page-section="true">
            <Col lg={8}>
              <Card>
                <CardBody>
                  <CardTitle
                    style={{
                      color:
                        layoutMode === layoutModeTypes.LIGHT
                          ? Colors.black
                          : Colors.white,
                    }}
                  >
                    {t("Overview")}
                  </CardTitle>
                  <div>
                    <div className="g_between">
                      <div className="d_center">
                        <h2 className="fw-bold text-center">
                          {dashboardData?.count_interviews_overview?.total_interview_capacity?.toLocaleString() ||
                            0}
                        </h2>
                        <h3 className="fw-bold">{t("Target Number")}</h3>
                      </div>
                      <div className="d_center">
                        <h2 className="fw-bold text-center">
                          {dashboardData?.count_interviews_overview?.total_booked_appointments?.toLocaleString() ||
                            0}
                        </h2>
                        <h3 className="fw-bold">{t("Booked Appointments")}</h3>
                      </div>
                    </div>
                  </div>
                  <hr />
                  <div className="d-flex py-4">
                    {interviewOverviews?.map(item => (
                      <div key={item?.id} className="flex-grow-1 text-center">
                        <h4 className="fw-bold">
                          {item?.count?.toLocaleString() || 0}
                        </h4>
                        <p>{t(item?.title)}</p>
                      </div>
                    ))}
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col lg={4}>
              <Card>
                <CardBody>
                  <CardTitle
                    style={{
                      color:
                        layoutMode === layoutModeTypes.LIGHT
                          ? Colors.black
                          : Colors.white,
                    }}
                  >
                    {t("Experience and New")}
                  </CardTitle>
                  {hasValidChartData(expNonExpChartData) ? (
                    <div className="ltr">
                      <DountChart
                        data={expNonExpChartData}
                        options={dountChartOptions}
                      />
                    </div>
                  ) : (
                    <div className="g_center h-75">
                      <h5>{t("No Data Available")}</h5>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
            {chartConfigs?.map(({ title, data }) => (
              <Col xl={4} key={title}>
                <Card>
                  <CardBody>
                    <CardTitle
                      style={{
                        color:
                          layoutMode === layoutModeTypes.LIGHT
                            ? Colors.black
                            : Colors.white,
                      }}
                    >
                      {t(title)}
                    </CardTitle>
                    {hasValidChartData(data) ? (
                      <div className="ltr">
                        <DountChart data={data} options={dountChartOptions} />
                      </div>
                    ) : (
                      <div className="g_center h-75">
                        <h5>{t("No Data Available")}</h5>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            ))}

            <Col xl={12}>
              <Card>
                <CardBody>
                  <CardTitle
                    style={{
                      color:
                        layoutMode === layoutModeTypes.LIGHT
                          ? Colors.black
                          : Colors.white,
                    }}
                  >
                    {t("Age")}
                  </CardTitle>
                  <div className="ltr">
                    <BarChart data={data} options={options} height={300} />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
          <EmploymentGoalsDashboard
            dashboardData={dashboardData}
            filters={filters}
          />
        </>
      )}
    </>
  )
}

export default RecruitmentStatistics
